'use client';

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  /** Whether the exit animation is in progress */
  exiting: boolean;
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType, duration?: number) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_VISIBLE = 3;
const DEFAULT_DURATION_MS = 3000;

let toastCounter = 0;

// ---------------------------------------------------------------------------
// Styling helpers
// ---------------------------------------------------------------------------

const STYLE_MAP: Record<
  ToastType,
  { bg: string; icon: string }
> = {
  success: {
    bg: 'bg-green-50 dark:bg-green-950/30',
    icon: '✓',
  },
  error: {
    bg: 'bg-red-50 dark:bg-red-950/30',
    icon: '✕',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    icon: '⚠',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    icon: 'ℹ',
  },
};

const ICON_COLOR: Record<ToastType, string> = {
  success: 'text-green-600 dark:text-green-400',
  error: 'text-red-600 dark:text-red-400',
  warning: 'text-amber-600 dark:text-amber-400',
  info: 'text-blue-600 dark:text-blue-400',
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  // Cleanup all timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
    };
  }, []);

  const removeToast = useCallback((id: string) => {
    // Start exit animation
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)),
    );
    // Remove from DOM after animation completes
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 200);
  }, []);

  const toast = useCallback(
    (
      message: string,
      type: ToastType = 'info',
      duration: number = DEFAULT_DURATION_MS,
    ) => {
      const id = `toast-${++toastCounter}`;
      const newToast: Toast = { id, type, message, exiting: false };

      setToasts((prev) => {
        // If at capacity, mark the oldest for removal
        const updated = [...prev, newToast];
        if (updated.length > MAX_VISIBLE) {
          const oldest = updated[0];
          // Schedule removal of oldest immediately
          setTimeout(() => removeToast(oldest.id), 0);
        }
        return updated;
      });

      // Auto-dismiss
      const timer = setTimeout(() => {
        removeToast(id);
        timersRef.current.delete(id);
      }, duration);
      timersRef.current.set(id, timer);
    },
    [removeToast],
  );

  const handleClose = useCallback(
    (id: string) => {
      // Clear auto-dismiss timer
      const timer = timersRef.current.get(id);
      if (timer) {
        clearTimeout(timer);
        timersRef.current.delete(id);
      }
      removeToast(id);
    },
    [removeToast],
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}

      {/* Toast container – bottom-right, stacked */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-[9999] flex flex-col-reverse gap-2"
      >
        {toasts.map((t) => {
          const style = STYLE_MAP[t.type];
          return (
            <div
              key={t.id}
              role="alert"
              className={[
                // Layout
                'pointer-events-auto flex w-80 items-start gap-3 rounded-2xl px-4 py-3 shadow-lg',
                // Colors
                style.bg,
                // Animation
                'transition-all duration-200 ease-in-out',
                t.exiting
                  ? 'translate-y-2 opacity-0'
                  : 'translate-y-0 opacity-100',
              ].join(' ')}
            >
              {/* Icon */}
              <span
                className={`mt-0.5 flex-shrink-0 text-lg font-bold leading-none ${ICON_COLOR[t.type]}`}
                aria-hidden="true"
              >
                {style.icon}
              </span>

              {/* Message */}
              <p className="flex-1 text-sm text-gray-800 dark:text-gray-100">
                {t.message}
              </p>

              {/* Close button */}
              <button
                type="button"
                onClick={() => handleClose(t.id)}
                className="flex-shrink-0 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200"
                aria-label="닫기"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
